import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useApplication } from '../../context/ApplicationContext';
import { aiService } from '../../services/aiService';
import toast from 'react-hot-toast';
import RatingVisualization from './RatingVisualization';
import EvaluationDisplay from '../common/EvaluationDisplay';
import useAdminMode from '../../hooks/useAdminMode';
import { 
  validateSchoolName, 
  validateMajor, 
  validateGPA, 
  validateTestScore, 
  validateNationality 
} from '../../utils/validation';
import { getSchoolPrompt } from '../../config/schoolPrompts';

const Step1Demographics = ({ schoolPrompt: legacySchoolPrompt }) => {
  const {
    demographics,
    academics,
    resume,
    essays,
    goals,
    additional,
    updateDemographics,
    updateAcademics,
    setEvaluation,
    setLoading,
    setStep,
    markStepComplete,
    goBack,
    canGoBack,
    resetToSchoolSelection,
    evaluations,
    trackEvaluationRequested,
    selectedSchool
  } = useApplication();

  const { isAdminMode } = useAdminMode();
  
  const [evaluation, setLocalEvaluation] = useState(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [showDebugPrompt, setShowDebugPrompt] = useState(false);
  const [lastPromptSent, setLastPromptSent] = useState('');
  const evaluationRef = useRef(null);

  // Auto-scroll to evaluation when it becomes available
  useEffect(() => {
    if (showEvaluation && evaluationRef.current) {
      setTimeout(() => {
        evaluationRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100); // Small delay to ensure DOM is updated
    }
  }, [showEvaluation]);

  const { register, handleSubmit, formState: { errors }, watch } = useForm({
    defaultValues: {
      // Demographics
      race: demographics.race || '',
      gender: demographics.gender || '',
      nationality: demographics.nationality || '',
      
      // Academics
      undergradSchool: academics.undergradSchool || '',
      undergradMajor: academics.undergradMajor || '',
      gpa: academics.gpa || '',
      testType: academics.testType || '',
      testScore: academics.testScore || '',
      additionalDegrees: academics.additionalDegrees || ''
    }
  });

  const testType = watch('testType');

    const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Track evaluation request
      trackEvaluationRequested(1);
      
      // Update application state
      updateDemographics({
        race: data.race,
        gender: data.gender,
        nationality: data.nationality
      });

      updateAcademics({
        undergradSchool: data.undergradSchool,
        undergradMajor: data.undergradMajor,
        gpa: data.gpa,
        testType: data.testType,
        testScore: data.testScore,
        additionalDegrees: data.additionalDegrees
      });

      // Use form data directly (state updates are async, so getCandidateData would return old data)
      const candidateData = {
        selectedSchool: selectedSchool?.value,
        demographics: {
          race: data.race,
          gender: data.gender,
          nationality: data.nationality
        },
        academics: {
          undergradSchool: data.undergradSchool,
          undergradMajor: data.undergradMajor,
          gpa: data.gpa,
          testType: data.testType,
          testScore: data.testScore,
          additionalDegrees: data.additionalDegrees
        },
        resume: resume,
        essays: essays || {},
        goals: goals || {},
        additional: additional || {}
      };
      
      // Debug logging to verify data
      console.log('🔍 Form Data Being Sent to AI:', candidateData);
      
      // Get step-specific prompt
      const schoolPrompt = getSchoolPrompt(selectedSchool?.value, 1);
      
      const prompt = `${schoolPrompt}

CANDIDATE INFORMATION:
${JSON.stringify(candidateData, null, 2)}

Please provide an initial Step 1 evaluation focusing only on this candidate's demographics and academic foundation for ${candidateData.selectedSchool} MBA admission. Note that work experience and essays will be reviewed in subsequent steps.`;

      setLastPromptSent(prompt);

      const result = await aiService.evaluateCandidate(prompt);
      
      if (result.success) {
        // Debug logging for rating
        console.log('🎯 Step1 Evaluation Result:', {
          success: result.success,
          rating: result.rating,
          ratingType: typeof result.rating,
          provider: result.provider,
          evaluationLength: result.evaluation?.length
        });
        
        // Process the evaluation to fix formatting
        const processedEvaluation = result.evaluation
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Convert **text** to <strong>text</strong>
          .replace(/\*([^*]+)\*/g, '<em>$1</em>'); // Convert *text* to <em>text</em>
        
        const newEvaluation = {
          step: 1,
          stepName: 'Demographics & Academics',
          evaluation: processedEvaluation,
          rating: result.rating,
          timestamp: new Date().toISOString()
        };
        
        console.log('📊 New Evaluation Object:', newEvaluation);
        
        setEvaluation(newEvaluation);
        setLocalEvaluation(newEvaluation);
        setShowEvaluation(true);
        
        if (result.provider === 'demo') {
          toast.success('Demo evaluation completed! Add OpenAI API key for real AI insights.');
        } else {
          toast.success('Step 1 evaluation completed!');
        }
      } else {
        toast.error('Failed to get evaluation. Please try again.');
      }
      
    } catch (error) {
      console.error('Error evaluating step 1:', error);
      toast.error('Failed to evaluate your profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToStep2 = () => {
    markStepComplete(1);
    setStep(2);
  };

  const raceOptions = [
    { value: 'asian', label: 'Asian' },
    { value: 'black', label: 'Black/African American' },
    { value: 'hispanic', label: 'Hispanic/Latino' },
    { value: 'white', label: 'White/Caucasian' },
    { value: 'native_american', label: 'Native American' },
    { value: 'pacific_islander', label: 'Pacific Islander' },
    { value: 'mixed', label: 'Mixed Race' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non_binary', label: 'Non-binary' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  const testTypeOptions = [
    { value: 'GMAT', label: 'GMAT (200-800, we\'ll auto-detect Classic vs Focus based on your score)' },
    { value: 'GRE', label: 'GRE (260-340, average ~325 for top schools)' }
  ];

  const getTestScorePlaceholder = () => {
    switch (testType) {
      case 'GMAT':
        return 'e.g., 720 (Classic) or 705 (Focus) - we\'ll auto-detect';
      case 'GRE':
        return 'e.g., 325 (or expected score if not taken yet)';
      default:
        return 'Enter your score or expected score';
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="card p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Step 1: Demographics & Academics
        </h2>
        <p className="text-gray-600 mb-8">
          Please provide your background and academic credentials. This helps us evaluate your fit with {selectedSchool?.label || 'the school'}'s diversity and academic standards.
        </p>

        {/* Debug Mode - Show AI Prompt */}
        {isAdminMode && (
          <div className="mb-6">
            <button
              onClick={() => setShowDebugPrompt(!showDebugPrompt)}
              className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors text-sm"
            >
              🔍 {showDebugPrompt ? 'Hide' : 'Show'} AI Prompt
            </button>
            
            {showDebugPrompt && lastPromptSent && (
              <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Prompt Sent to AI:</h4>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto max-h-96">
                  {lastPromptSent}
                </pre>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Demographics Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Demographics</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Race/Ethnicity
                </label>
                <select
                  {...register('race', { required: 'Race/Ethnicity is required' })}
                  className="form-select"
                >
                  <option value="">Select...</option>
                  {raceOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.race && <p className="text-red-500 text-sm mt-1">{errors.race.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  {...register('gender', { required: 'Gender is required' })}
                  className="form-select"
                >
                  <option value="">Select...</option>
                  {genderOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender.message}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nationality
                </label>
                <input
                  {...register('nationality', { 
                    required: 'Nationality is required',
                    validate: (value) => {
                      const validation = validateNationality(value);
                      return validation.isValid || validation.message;
                    }
                  })}
                  className="form-input"
                  placeholder="e.g., American, Indian, Brazilian, French, Chinese"
                />
                {errors.nationality && <p className="text-red-500 text-sm mt-1">{errors.nationality.message}</p>}
              </div>
            </div>
          </div>

          {/* Academics Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Academic Background</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Undergraduate School
                </label>
                <input
                  {...register('undergradSchool', { 
                    required: 'Undergraduate school is required',
                    validate: (value) => {
                      const validation = validateSchoolName(value);
                      return validation.isValid || validation.message;
                    }
                  })}
                  className="form-input"
                  placeholder="e.g., Harvard University, IIT Delhi, University of Oxford"
                />
                {errors.undergradSchool && <p className="text-red-500 text-sm mt-1">{errors.undergradSchool.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Undergraduate Major
                </label>
                <input
                  {...register('undergradMajor', { 
                    required: 'Undergraduate major is required',
                    validate: (value) => {
                      const validation = validateMajor(value);
                      return validation.isValid || validation.message;
                    }
                  })}
                  className="form-input"
                  placeholder="e.g., Computer Science, Economics, Engineering"
                />
                {errors.undergradMajor && <p className="text-red-500 text-sm mt-1">{errors.undergradMajor.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GPA (or equivalent)
                </label>
                <input
                  {...register('gpa', { 
                    required: 'GPA is required',
                    validate: (value) => {
                      const validation = validateGPA(value);
                      return validation.isValid || validation.message;
                    }
                  })}
                  className="form-input"
                  placeholder="e.g., 3.8/4.0, 8.5/10, First Class Honours"
                />
                {errors.gpa && <p className="text-red-500 text-sm mt-1">{errors.gpa.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Type
                </label>
                <select
                  {...register('testType', { required: 'Test type is required' })}
                  className="form-select"
                >
                  <option value="">Select test type...</option>
                  {testTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.testType && <p className="text-red-500 text-sm mt-1">{errors.testType.message}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Score (actual or expected)
                </label>
                <input
                  {...register('testScore', { 
                    required: 'Test score is required',
                    validate: (value) => {
                      const validation = validateTestScore(value, testType);
                      return validation.isValid || validation.message;
                    }
                  })}
                  className="form-input"
                  placeholder={getTestScorePlaceholder()}
                />
                {errors.testScore && <p className="text-red-500 text-sm mt-1">{errors.testScore.message}</p>}
                {testType && (
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your actual score or expected score if you haven't taken the test yet
                  </p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Degrees (Optional)
                </label>
                <textarea
                  {...register('additionalDegrees')}
                  className="form-textarea"
                  rows="2"
                  placeholder="e.g., Master's in Engineering, CPA, CFA, etc."
                />
              </div>
            </div>
          </div>

          {/* Navigation and Submit Buttons */}
          <div className="flex justify-between items-center">
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={resetToSchoolSelection}
                className="btn btn-secondary px-4 py-2"
              >
                ← Start Over
              </button>
            </div>
            <button
              type="submit"
              className="btn btn-primary px-8 py-3"
              disabled={false}
            >
              Get Evaluation
            </button>
          </div>
        </form>

        {/* Evaluation Results */}
        {showEvaluation && (evaluations?.step1 || evaluations?.[`step1`] || evaluation) && (
          <div ref={evaluationRef} className="mt-8">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Demographics & Academics Evaluation
              </h3>
              <div className="mb-4">
                {(() => {
                  // Check both possible evaluation sources
                  const storedEval = evaluations?.step1 || evaluations?.[`step1`];
                  const currentEval = storedEval || evaluation;
                  return <RatingVisualization rating={currentEval?.rating} />;
                })()}
              </div>
            </div>
            
            <EvaluationDisplay 
              evaluation={(evaluations?.step1 || evaluations?.[`step1`] || evaluation)?.evaluation}
              rating={(evaluations?.step1 || evaluations?.[`step1`] || evaluation)?.rating}
            />


            
            <div className="mt-6 flex justify-between">
              <button
                type="button"
                onClick={goBack}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={!canGoBack()}
              >
                ← Edit Details
              </button>
              <button
                onClick={handleProceedToStep2}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Continue to Work Experience →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step1Demographics; 